import React, { useState, useRef, useEffect } from 'react';
import styles from './PlayerDropdown.module.css';
import { DetailedRoundData } from '../utils/types';

interface PlayerDropdownProps {
  selectedPlayerIndices: number[];
  roundData?: DetailedRoundData;
  onPlayerSelectionsChange: (indices: number[]) => void;
  onUtilityTableOpen: () => void;
}

const PlayerDropdown: React.FC<PlayerDropdownProps> = ({
  selectedPlayerIndices,
  roundData,
  onPlayerSelectionsChange,
  onUtilityTableOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const players = roundData?.frames[0]?.players || [];
  const isLoading = !roundData;

  const handlePlayerToggle = (index: number) => {
    const newSelection = selectedPlayerIndices.includes(index)
      ? selectedPlayerIndices.filter(i => i !== index)
      : [...selectedPlayerIndices, index];
    
    // Ensure at least one player remains selected
    if (newSelection.length > 0) {
      onPlayerSelectionsChange(newSelection);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button 
        className={styles.playerButton}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isLoading ? (
          <div className={styles.spinner} />
        ) : (
          selectedPlayerIndices.length === 1 ? (
            <img 
              src={`/agents/${players[selectedPlayerIndices[0]]?.agent?.toLowerCase()}.png`}
              alt={players[selectedPlayerIndices[0]]?.agent || ''}
              style={{ 
                width: '20px', 
                height: '20px',
                transform: selectedPlayerIndices[0] >= 5 ? 'scaleX(-1)' : 'none',
              }}
            />
          ) : (
            <span className={styles.multipleSelected}>
              {selectedPlayerIndices.length}
            </span>
          )
        )}
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          <div className={styles.team}>
            {players.slice(0, 5).map((player, index) => (
              <button 
                key={index}
                onClick={() => handlePlayerToggle(index)}
                className={`${styles.playerOption} ${selectedPlayerIndices.includes(index) ? styles.selected : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedPlayerIndices.includes(index)}
                  onChange={() => {}} // Handled by button click
                  className={styles.checkbox}
                />
                <img 
                  src={`/agents/${player.agent?.toLowerCase()}.png`}
                  alt={player.agent}
                  className={styles.agentIcon}
                />
                <span>{player.name}</span>
              </button>
            ))}
          </div>

          <div className={styles.divider} />

          <div className={styles.team}>
            {players.slice(5, 10).map((player, index) => (
              <button 
                key={index + 5}
                onClick={() => handlePlayerToggle(index + 5)}
                className={`${styles.playerOption} ${selectedPlayerIndices.includes(index + 5) ? styles.selected : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedPlayerIndices.includes(index + 5)}
                  onChange={() => {}} // Handled by button click
                  className={styles.checkbox}
                />
                <img 
                  src={`/agents/${player.agent?.toLowerCase()}.png`}
                  alt={player.agent}
                  className={styles.agentIcon}
                  style={{ transform: 'scaleX(-1)' }}
                />
                <span>{player.name}</span>
              </button>
            ))}
          </div>

          <div className={styles.divider} />

          <div className={styles.team}>
            <button 
              onClick={() => {
                onUtilityTableOpen();
                setIsOpen(false);
              }}
              className={styles.playerOption}
            >
              <i className="bi bi-table" />
              <span style={{ paddingLeft: '12px' }}>Utility table</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerDropdown; 